// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React, { createContext, useContext, useReducer, useCallback, useEffect, useRef } from 'react';
import type { LedgerEvent, LedgerState, SyncStatus, AuthState, SettingsValues } from '../types.js';
import { SETTINGS_DEFAULTS } from '../types.js';
import { replayEvents, mergeEvents, applyEvent, projectSettings, applySettingsEvent } from '../lib/eventLog.js';
import { saveSession, saveTokens, saveKeyData, clearSession, loadSession, peekStoredUsername, saveSyncUrl, peekSyncUrl, tokenExpiresAt, type StoredSession } from '../lib/session.js';
import { refreshSession, logoutSession } from '../lib/syncClient.js';
import { resolveDefaultSyncUrl } from '../lib/apiConfig.js';
import { IS_NATIVE } from '../native/platform.js';
import { isBiometricEnabled, setBiometricEnabled } from '../native/biometric.js';

// --- State shape ---

export interface AppState {
  auth: AuthState;
  events: LedgerEvent[];
  ledger: LedgerState;
  appSettings: SettingsValues; // derived from events via projectSettings
  sync: SyncStatus;
  recentAccountIds: string[]; // recently viewed account IDs (for sidebar)
}

// --- Actions ---

export type AppAction =
  | { type: 'AUTH_SUCCESS'; username: string; token: string; refreshToken: string; keyData: ArrayBuffer; syncUrl: string }
  | { type: 'AUTH_LOGOUT' }
  // Silent access-token rotation via the refresh token — updates tokens in place
  // without touching the ledger key or re-running the login flow.
  | { type: 'TOKEN_REFRESHED'; token: string; refreshToken: string }
  // Ledger key rotation (SEC-07): the DEK changed but the events are unchanged.
  | { type: 'KEY_ROTATED'; keyData: ArrayBuffer }
  // Point the app at a different sync server (native shells; persisted separately
  // from the session so it survives logout — see session.ts clearSession).
  | { type: 'SET_SYNC_URL'; syncUrl: string }
  // Async-bootstrap actions (see the bootstrap effect in AppProvider).
  | { type: 'SESSION_RESTORED'; session: StoredSession }
  | { type: 'BOOTSTRAP_DONE'; expired: boolean }
  | { type: 'SESSION_LOCKED'; username: string }
  | { type: 'EVENTS_LOADED'; events: LedgerEvent[] }
  | { type: 'EVENT_APPENDED'; event: LedgerEvent }
  | { type: 'SYNC_START' }
  | { type: 'SYNC_SUCCESS' }
  | { type: 'SYNC_ERROR'; message: string }
  | { type: 'ACCOUNT_VIEWED'; accountId: string };

// --- Initial state ---

const emptyLedger: LedgerState = {
  accounts: new Map(),
  transactions: new Map(),
  scheduledTransactions: new Map(),
  budgets: new Map(),
  prices: new Map(),
  settings: { homeCurrency: 'USD' },
  lastSequence: 0,
};

const defaultSyncUrl = resolveDefaultSyncUrl();

const initialState: AppState = {
  auth: {
    isAuthenticated: false,
    username: null,
    token: null,
    refreshToken: null,
    keyData: null,
    syncUrl: defaultSyncUrl,
    sessionExpired: false,
    bootstrapped: false,
    locked: false,
  },
  events: [],
  ledger: emptyLedger,
  appSettings: { ...SETTINGS_DEFAULTS, coinbaseAccountMapping: {} },
  sync: {
    status: 'idle',
    lastSyncAt: null,
    errorMessage: null,
  },
  recentAccountIds: [],
};

// --- Reducer ---

function reducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'AUTH_SUCCESS':
      return {
        ...state,
        auth: {
          isAuthenticated: true,
          username: action.username,
          token: action.token,
          refreshToken: action.refreshToken,
          keyData: action.keyData,
          syncUrl: action.syncUrl,
          sessionExpired: false,
          bootstrapped: true,
          locked: false,
        },
        events: [],
        ledger: emptyLedger,
        appSettings: { ...SETTINGS_DEFAULTS, coinbaseAccountMapping: {} },
        recentAccountIds: [],
      };

    case 'TOKEN_REFRESHED':
      return {
        ...state,
        auth: { ...state.auth, token: action.token, refreshToken: action.refreshToken },
      };

    case 'KEY_ROTATED':
      return { ...state, auth: { ...state.auth, keyData: action.keyData } };

    case 'SET_SYNC_URL':
      return { ...state, auth: { ...state.auth, syncUrl: action.syncUrl } };

    // Restore a persisted session (web auto-restore, or native after biometric
    // unlock). Unlike AUTH_SUCCESS this does not touch storage — the session is
    // already there — it just lifts it into app state.
    case 'SESSION_RESTORED':
      return {
        ...state,
        auth: {
          isAuthenticated: true,
          username: action.session.username,
          token: action.session.token,
          refreshToken: action.session.refreshToken,
          keyData: action.session.keyData,
          syncUrl: action.session.syncUrl,
          sessionExpired: false,
          bootstrapped: true,
          locked: false,
        },
        events: [],
        ledger: emptyLedger,
        appSettings: { ...SETTINGS_DEFAULTS, coinbaseAccountMapping: {} },
        recentAccountIds: [],
      };

    // Bootstrap finished with no restorable session — show login (optionally
    // flagging that a stored token had expired).
    case 'BOOTSTRAP_DONE':
      return {
        ...state,
        auth: {
          ...initialState.auth,
          syncUrl: state.auth.syncUrl,
          sessionExpired: action.expired,
          bootstrapped: true,
          locked: false,
        },
      };

    // Native cold start with a valid persisted session behind a biometric gate:
    // present the lock screen rather than the login form.
    case 'SESSION_LOCKED':
      return {
        ...state,
        auth: {
          ...initialState.auth,
          username: action.username,
          syncUrl: state.auth.syncUrl,
          bootstrapped: true,
          locked: true,
        },
      };

    case 'AUTH_LOGOUT':
      return {
        ...initialState,
        auth: {
          ...initialState.auth,
          syncUrl: state.auth.syncUrl,
          sessionExpired: false,
          bootstrapped: true,
          locked: false,
        },
      };

    case 'EVENTS_LOADED': {
      const merged = mergeEvents(state.events, action.events);
      return {
        ...state,
        events: merged,
        ledger: replayEvents(merged),
        appSettings: projectSettings(merged),
      };
    }

    case 'EVENT_APPENDED': {
      const merged = mergeEvents(state.events, [action.event]);
      return {
        ...state,
        events: merged,
        ledger: applyEvent(state.ledger, action.event),
        appSettings: applySettingsEvent(state.appSettings, action.event),
      };
    }

    case 'SYNC_START':
      return {
        ...state,
        sync: { ...state.sync, status: 'syncing', errorMessage: null },
      };

    case 'SYNC_SUCCESS':
      return {
        ...state,
        sync: { status: 'success', lastSyncAt: new Date().toISOString(), errorMessage: null },
      };

    case 'SYNC_ERROR':
      return {
        ...state,
        sync: { ...state.sync, status: 'error', errorMessage: action.message },
      };

    case 'ACCOUNT_VIEWED': {
      const prev = state.recentAccountIds.filter((id) => id !== action.accountId);
      return {
        ...state,
        recentAccountIds: [action.accountId, ...prev].slice(0, 5),
      };
    }

    default:
      return state;
  }
}

// --- Context ---

interface AppContextValue {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
}

// Exported so tests / preview harnesses can inject a pre-built state without
// going through the async auth bootstrap.
export const AppContext = createContext<AppContextValue | null>(null);

export function AppProvider({ children }: { children: React.ReactNode }) {
  // DECISION: start unauthenticated. Session restore reads secure storage, which
  // is async on native (Keychain/Keystore), so it cannot run in the reducer's
  // lazy initializer. The bootstrap effect below resolves it; until then the app
  // renders a neutral loading gate (see App.tsx) rather than flashing login.
  const [state, baseDispatch] = useReducer(reducer, initialState);

  // Latest state, for side effects (e.g. server-side logout revocation) that run
  // in the dispatch wrapper and need the pre-logout tokens.
  const stateRef = useRef(state);
  stateRef.current = state;

  const dispatch = useCallback(
    (action: AppAction) => {
      // Persist to secure storage as a side effect. On web these writes are
      // effectively synchronous (sessionStorage.setItem runs before the resolved
      // Promise), so web behavior is unchanged; on native they are Keychain
      // writes we intentionally do not block the UI on.
      if (action.type === 'AUTH_SUCCESS') {
        void saveSession(action.token, action.refreshToken, action.keyData, action.username, action.syncUrl);
      } else if (action.type === 'TOKEN_REFRESHED') {
        void saveTokens(action.token, action.refreshToken);
      } else if (action.type === 'KEY_ROTATED') {
        void saveKeyData(action.keyData);
      } else if (action.type === 'AUTH_LOGOUT') {
        // Best-effort server-side revoke of the refresh token before wiping local
        // state, so a logged-out session can't be resurrected (SEC-03).
        const { syncUrl, refreshToken } = stateRef.current.auth;
        if (refreshToken) void logoutSession(syncUrl, refreshToken);
        void clearSession();
        // The persisted key is gone; a stale biometric opt-in would strand the
        // user on a lock screen with nothing to unlock, so clear it too.
        void setBiometricEnabled(false);
      } else if (action.type === 'SET_SYNC_URL') {
        // Persist the chosen server URL so it survives reloads and (unlike the
        // session secrets) logout — the native shells rely on it to find the API.
        void saveSyncUrl(action.syncUrl);
      }
      baseDispatch(action);
    },
    [baseDispatch],
  );

  // One-shot session bootstrap.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      // Seed a previously-configured sync-server URL (survives logout) so the
      // native shells target the right API before any login. No-op if unset.
      const savedUrl = await peekSyncUrl();
      if (cancelled) return;
      if (savedUrl) baseDispatch({ type: 'SET_SYNC_URL', syncUrl: savedUrl });

      if (IS_NATIVE) {
        // DECISION (native security model): the on-device key persists in the
        // Keychain/Keystore across launches, but we only ever lift it into memory
        // behind a biometric gate. If the user opted into biometric unlock and a
        // valid session exists, show the lock screen. Otherwise require a full
        // password login and proactively clear the lingering key so it never
        // sits unlocked-by-default — the safe default for a finance app.
        const [bioEnabled, username] = await Promise.all([
          isBiometricEnabled(),
          peekStoredUsername(),
        ]);
        if (cancelled) return;
        if (username && bioEnabled) {
          baseDispatch({ type: 'SESSION_LOCKED', username });
          return;
        }
        if (username && !bioEnabled) {
          await clearSession();
        }
        if (cancelled) return;
        baseDispatch({ type: 'BOOTSTRAP_DONE', expired: false });
        return;
      }

      // Web: auto-restore the per-tab session exactly as before.
      const result = await loadSession();
      if (cancelled) return;
      if (result.session) {
        baseDispatch({ type: 'SESSION_RESTORED', session: result.session });
      } else {
        baseDispatch({ type: 'BOOTSTRAP_DONE', expired: result.expired });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Silent access-token refresh (SEC-03). Access JWTs live ~30 min; schedule a
  // refresh ~2 min before expiry (or immediately if a restored token is already
  // expired). On refresh failure the session is dead — log out.
  const { isAuthenticated, token, refreshToken, syncUrl } = state.auth;
  useEffect(() => {
    if (!isAuthenticated || !token || !refreshToken) return;
    let cancelled = false;

    const doRefresh = async () => {
      try {
        const res = await refreshSession(syncUrl, refreshToken);
        if (!cancelled) {
          baseDispatch({ type: 'TOKEN_REFRESHED', token: res.token, refreshToken: res.refreshToken });
        }
      } catch {
        if (!cancelled) baseDispatch({ type: 'AUTH_LOGOUT' });
      }
    };

    const expiresAt = tokenExpiresAt(token);
    const delay = expiresAt ? Math.max(0, expiresAt - Date.now() - 120_000) : 0;
    const timer = setTimeout(() => void doRefresh(), delay);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [isAuthenticated, token, refreshToken, syncUrl]);

  return <AppContext.Provider value={{ state, dispatch }}>{children}</AppContext.Provider>;
}

export function useAppState(): AppState {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useAppState must be used within AppProvider');
  return ctx.state;
}

export function useAppDispatch(): React.Dispatch<AppAction> {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useAppDispatch must be used within AppProvider');
  return ctx.dispatch;
}

export function useApp(): AppContextValue {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}
